import * as React from 'react';
import * as KendoLayout from '@progress/kendo-react-layout';
import { AccentButton } from '../controls/AccentButton';
import { t, accentUtils, showOK } from '../services/HelperService';
import { AccentDialog } from './AccentDialog';
import { isMobileOnly } from '../controls/AccentDisplay';
import { RTLDocumentGroupPreview } from '../controls/documents/DocumentPreview';
import { queryNoTracking } from '../services/DataService';
import { AccentComboUnbound } from '../controls/unbound/AccentComboUnbound';
import { generateTemplateContent } from '../services/TemplateService';
import { looksLikeHtml } from '../controls/chat/ChatHtml';

const query_Contact_Info = queryNoTracking("Contact_Info");
const query_ChatAll = queryNoTracking("Chat_All");
const query_NormalisePhoneNumber = queryNoTracking("NormalisePhoneNumber");

// WhatsApp Recipient Token Component
const WhatsAppRecipientToken = ({ contact, selectedNumber, onNumberChange, onRemove }) => {
    const [isOpen, setIsOpen] = React.useState(false);
    const dropdownRef = React.useRef(null);

    // Get available phone numbers
    const phoneNumbers = React.useMemo(() => {
        const numbers = [];
        if (contact.Mobile) numbers.push({ type: 'Mobile', value: contact.Mobile });
        if (contact.WorkPhone) numbers.push({ type: 'Work', value: contact.WorkPhone });
        if (contact.HomePhone) numbers.push({ type: 'Home', value: contact.HomePhone });
        return numbers;
    }, [contact]);

    const hasMultipleNumbers = phoneNumbers.length > 1;
    const currentNumber = phoneNumbers.find(n => n.value === selectedNumber) || phoneNumbers[0];
    const hasNoNumbers = phoneNumbers.length === 0;

    // Close dropdown when clicking outside
    React.useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [isOpen]);

    return (
        <div
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                backgroundColor: hasNoNumbers ? '#ffebee' : '#e7f3ff',
                border: hasNoNumbers ? '1px solid #ef5350' : '1px solid #90caf9',
                borderRadius: '16px',
                padding: '4px 8px 4px 12px',
                margin: '2px',
                fontSize: '14px',
                gap: '6px',
                position: 'relative'
            }}
        >
            <span style={{ fontWeight: '500', color: hasNoNumbers ? '#c62828' : '#1976d2' }}>
                {contact.Name || contact.FullName}
            </span>

            {hasNoNumbers ? (
                <span style={{ fontSize: '12px', color: '#c62828', fontStyle: 'italic' }}>
                    (no phone number)
                </span>
            ) : hasMultipleNumbers ? (
                <div ref={dropdownRef} style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
                    <button
                        onClick={() => setIsOpen(!isOpen)}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            background: 'white',
                            border: '1px solid #90caf9',
                            borderRadius: '12px',
                            padding: '2px 6px',
                            fontSize: '12px',
                            cursor: 'pointer',
                            color: '#555'
                        }}
                    >
                        <span>📱 {currentNumber.type}: {currentNumber.value}</span>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="6 9 12 15 18 9"></polyline>
                        </svg>
                    </button>

                    {isOpen && (
                        <div
                            style={{
                                position: 'fixed',
                                top: dropdownRef.current?.getBoundingClientRect().bottom + 4 || 0,
                                left: dropdownRef.current?.getBoundingClientRect().left || 0,
                                backgroundColor: 'white',
                                border: '1px solid #ccc',
                                borderRadius: '4px',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                zIndex: 10000,
                                minWidth: '180px'
                            }}
                        >
                            {phoneNumbers.map((number) => (
                                <button
                                    key={number.value}
                                    onClick={() => {
                                        onNumberChange(number.value);
                                        setIsOpen(false);
                                    }}
                                    style={{
                                        display: 'block',
                                        width: '100%',
                                        textAlign: 'left',
                                        padding: '8px 12px',
                                        border: 'none',
                                        background: selectedNumber === number.value ? '#f0f0f0' : 'white',
                                        cursor: 'pointer',
                                        fontSize: '13px',
                                        fontWeight: selectedNumber === number.value ? '500' : 'normal'
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = selectedNumber === number.value ? '#f0f0f0' : 'white';
                                    }}
                                >
                                    📱 {number.type}: {number.value}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            ) : (
                <span style={{ fontSize: '12px', color: '#666' }}>
                    📱 {currentNumber.type}: {currentNumber.value}
                </span>
            )}

            <button
                onClick={onRemove}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '2px',
                    marginLeft: '2px',
                    color: hasNoNumbers ? '#c62828' : '#1976d2',
                    borderRadius: '50%'
                }}
                onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = hasNoNumbers ? 'rgba(198, 40, 40, 0.1)' : 'rgba(25, 118, 210, 0.1)';
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                }}
            >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
            </button>
        </div>
    );
};

// WhatsApp Recipient Selector Component
const WhatsAppRecipientSelector = ({ to, onChange }) => {
    const [recipients, setRecipients] = React.useState(to || []);
    const [searchTerm, setSearchTerm] = React.useState('');
    const [showSuggestions, setShowSuggestions] = React.useState(false);
    const [contactSuggestions, setContactSuggestions] = React.useState([]);
    const [isLoadingSuggestions, setIsLoadingSuggestions] = React.useState(false);
    const inputRef = React.useRef(null);

    // Sync recipients when to prop changes
    React.useEffect(() => {
        if (to && to.length > 0) {
            setRecipients(to);
        }
    }, [to]);

    // Sync recipients with parent
    React.useEffect(() => {
        if (onChange) {
            onChange(recipients);
        }
    }, [recipients]);

    // Load contact suggestions as user types
    React.useEffect(() => {
        if (!searchTerm || searchTerm.length < 2) {
            setContactSuggestions([]);
            return;
        }

        let cancelled = false;
        setIsLoadingSuggestions(true);

        const loadContacts = async () => {
            try {
                const results = await queryNoTracking("Contact_SelectList").getAll({
                    includeID: null,
                    companyID: null
                });

                if (!cancelled) {
                    const searchLower = searchTerm.toLowerCase();
                    // Filter contacts by search term and exclude already selected
                    const filtered = (Array.isArray(results) ? results : []).filter(contact => {
                        const alreadySelected = recipients.some(r => r.contact.ID === contact.ID);
                        if (alreadySelected) return false;

                        const fullName = (contact.FullName || '').toLowerCase();
                        return fullName.includes(searchLower);
                    });
                    setContactSuggestions(filtered);
                }
            } catch (err) {
                console.error('Error loading contacts:', err);
                if (!cancelled) setContactSuggestions([]);
            } finally {
                if (!cancelled) setIsLoadingSuggestions(false);
            }
        };

        const timeoutId = setTimeout(loadContacts, 300);
        return () => {
            cancelled = true;
            clearTimeout(timeoutId);
        };
    }, [searchTerm, recipients]);

    const addRecipient = async (contact) => {
        try {
            // Load full contact info to get phone numbers
            const contactInfo = await query_Contact_Info.getFirstOrDefault({ id: contact.ID });

            const defaultNumber = contactInfo.Mobile || contactInfo.WorkPhone || contactInfo.HomePhone || null;

            setRecipients([...recipients, {
                contact: { ...contact, ...contactInfo },
                selectedNumber: defaultNumber
            }]);
            setSearchTerm('');
            setShowSuggestions(false);
            inputRef.current?.focus();
        } catch (err) {
            console.error('Error loading contact info:', err);
        }
    };

    const removeRecipient = (index) => {
        setRecipients(recipients.filter((_, i) => i !== index));
    };

    const updateRecipientNumber = (index, newNumber) => {
        const updated = [...recipients];
        updated[index].selectedNumber = newNumber;
        setRecipients(updated);
    };

    return (
        <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                {t("application_strings.application.general.emailTo")}
            </label>

            <div
                style={{
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    padding: '6px',
                    minHeight: '42px',
                    backgroundColor: 'white',
                    cursor: 'text'
                }}
                onClick={() => inputRef.current?.focus()}
            >
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', alignItems: 'center' }}>
                    {recipients.map((recipient, index) => (
                        <WhatsAppRecipientToken
                            key={`${recipient.contact.ID}-${index}`}
                            contact={recipient.contact}
                            selectedNumber={recipient.selectedNumber}
                            onNumberChange={(newNumber) => updateRecipientNumber(index, newNumber)}
                            onRemove={() => removeRecipient(index)}
                        />
                    ))}

                    <div style={{ position: 'relative', flex: '1 1 200px', minWidth: '200px' }}>
                        <input
                            ref={inputRef}
                            type="text"
                            value={searchTerm}
                            onChange={(e) => {
                                setSearchTerm(e.target.value);
                                setShowSuggestions(true);
                            }}
                            onFocus={() => setShowSuggestions(true)}
                            onBlur={() => {
                                // Delay to allow click on suggestion
                                setTimeout(() => setShowSuggestions(false), 200);
                            }}
                            placeholder={recipients.length === 0 ? "Start typing a name..." : ""}
                            style={{
                                border: 'none',
                                outline: 'none',
                                padding: '6px',
                                fontSize: '14px',
                                width: '100%',
                                backgroundColor: 'transparent'
                            }}
                        />

                        {showSuggestions && (searchTerm.length >= 2) && (
                            <div
                                style={{
                                    position: 'fixed',
                                    top: inputRef.current?.getBoundingClientRect().bottom + 4 || 0,
                                    left: inputRef.current?.getBoundingClientRect().left || 0,
                                    width: inputRef.current?.getBoundingClientRect().width || 'auto',
                                    backgroundColor: 'white',
                                    border: '1px solid #ccc',
                                    borderRadius: '4px',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                    maxHeight: '200px',
                                    overflowY: 'auto',
                                    zIndex: 10000
                                }}
                            >
                                {isLoadingSuggestions ? (
                                    <div style={{ padding: '10px 12px', fontSize: '13px', color: '#666' }}>
                                        Loading...
                                    </div>
                                ) : contactSuggestions.length === 0 ? (
                                    <div style={{ padding: '10px 12px', fontSize: '13px', color: '#666' }}>
                                        No contacts found
                                    </div>
                                ) : (
                                    contactSuggestions.map((contact) => (
                                        <button
                                            key={contact.ID}
                                            onClick={() => addRecipient(contact)}
                                            style={{
                                                display: 'block',
                                                width: '100%',
                                                textAlign: 'left',
                                                padding: '10px 12px',
                                                border: 'none',
                                                background: 'white',
                                                cursor: 'pointer',
                                                fontSize: '14px',
                                                borderBottom: '1px solid #eee'
                                            }}
                                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                                        >
                                            <div style={{ fontWeight: '500' }}>{contact.FullName}</div>
                                        </button>
                                    ))
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

// Chat Link Details Component
const ChatLinkDetails = ({ recipients, linkToChats }) => {
    const [isExpanded, setIsExpanded] = React.useState(false);
    const [chatDetails, setChatDetails] = React.useState([]);
    const [isLoading, setIsLoading] = React.useState(false);

    // Cache to avoid re-fetching the same phone numbers
    const chatCacheRef = React.useRef(new Map());

    // Load chat details when expanded
    React.useEffect(() => {
        if (!isExpanded || !linkToChats) return;

        let cancelled = false;
        setIsLoading(true);

        const loadChatDetails = async () => {
            try {
                const details = await Promise.all(
                    recipients.map(async (recipient) => {
                        if (!recipient.selectedNumber) {
                            return {
                                recipient,
                                hasChat: false,
                                chat: null
                            };
                        }

                        try {
                            // Check cache first
                            const cacheKey = recipient.selectedNumber;
                            if (chatCacheRef.current.has(cacheKey)) {
                                const cached = chatCacheRef.current.get(cacheKey);
                                return {
                                    recipient,
                                    hasChat: cached.hasChat,
                                    chat: cached.chat
                                };
                            }

                            // Normalize phone number to E.164 format
                            const normalizeResult = await query_NormalisePhoneNumber.getFirstOrDefault({
                                phoneNumber: recipient.selectedNumber
                            });
                            const normalizedPhone = normalizeResult?.Normalised || recipient.selectedNumber;

                            // Query for existing open WhatsApp chat
                            const chatResult = await query_ChatAll.getAll({
                                skip: 0,
                                take: 1,
                                contactPoint: normalizedPhone,
                                channel: 'whatsapp',
                                status: ['Open', 'Pending', 'On Hold'],
                                isArchived: false
                            });

                            // Handle the response - query returns an array, first element is { data: [...], total: number }
                            let chats = [];
                            if (Array.isArray(chatResult) && chatResult.length > 0) {
                                const result = chatResult[0];
                                chats = result?.data || result?.Data || [];
                            }

                            let chatInfo = null;
                            if (chats.length > 0) {
                                const chat = chats[0];
                                chatInfo = {
                                    id: chat.ID || chat.id,
                                    chatID: chat.ChatID || chat.chatID,
                                    subject: chat.Subject || chat.subject || 'Untitled Chat',
                                    lastActivityAt: chat.LastActivityAt || chat.lastActivityAt,
                                    contactName: chat.ContactName || chat.contactName
                                };
                            }

                            // Cache the result
                            chatCacheRef.current.set(cacheKey, {
                                hasChat: chatInfo !== null,
                                chat: chatInfo
                            });

                            return {
                                recipient,
                                hasChat: chatInfo !== null,
                                chat: chatInfo
                            };
                        } catch (err) {
                            console.error(`Error loading chat for ${recipient.contact.FullName}:`, err);
                            return {
                                recipient,
                                hasChat: false,
                                chat: null,
                                error: true
                            };
                        }
                    })
                );

                if (!cancelled) {
                    setChatDetails(details);
                }
            } catch (err) {
                console.error('Error loading chat details:', err);
            } finally {
                if (!cancelled) {
                    setIsLoading(false);
                }
            }
        };

        loadChatDetails();

        return () => {
            cancelled = true;
        };
    }, [isExpanded, recipients, linkToChats]);

    if (!linkToChats) return null;

    const formatTimeAgo = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMins / 60);
        const diffDays = Math.floor(diffHours / 24);

        if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
        if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
        return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    };

    const withChats = chatDetails.filter(d => d.hasChat);
    const withoutChats = chatDetails.filter(d => !d.hasChat && !d.error);
    const withErrors = chatDetails.filter(d => d.error);

    return (
        <div style={{ marginBottom: '20px' }}>
            <button
                onClick={() => setIsExpanded(!isExpanded)}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '8px 12px',
                    border: '1px solid #90caf9',
                    borderRadius: '4px',
                    backgroundColor: '#e3f2fd',
                    color: '#1565c0',
                    fontSize: '13px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    width: '100%',
                    justifyContent: 'space-between'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#bbdefb'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#e3f2fd'}
            >
                <span>View details for {recipients.length} recipient{recipients.length !== 1 ? 's' : ''}</span>
                <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    style={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}
                >
                    <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
            </button>

            {isExpanded && (
                <div style={{
                    marginTop: '12px',
                    border: '1px solid #e0e0e0',
                    borderRadius: '4px',
                    padding: '12px',
                    backgroundColor: '#fafafa'
                }}>
                    {isLoading ? (
                        <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                            Loading chat information...
                        </div>
                    ) : (
                        <>
                            {withChats.length > 0 && (
                                <div style={{ marginBottom: withoutChats.length > 0 ? '16px' : '0' }}>
                                    <div style={{ fontSize: '13px', fontWeight: '600', color: '#2e7d32', marginBottom: '8px' }}>
                                        ✓ Recipients with existing chats ({withChats.length}):
                                    </div>
                                    {withChats.map((detail, index) => (
                                        <div
                                            key={index}
                                            style={{
                                                padding: '10px',
                                                marginBottom: '8px',
                                                backgroundColor: '#e8f5e9',
                                                border: '1px solid #c8e6c9',
                                                borderRadius: '4px',
                                                fontSize: '13px'
                                            }}
                                        >
                                            <div style={{ fontWeight: '500', marginBottom: '4px' }}>
                                                {detail.recipient.contact.FullName || detail.recipient.contact.Name}
                                            </div>
                                            <div style={{ color: '#555', fontSize: '12px' }}>
                                                Chat #{detail.chat.id}: "{detail.chat.subject}"
                                            </div>
                                            <div style={{ color: '#666', fontSize: '11px', marginTop: '2px' }}>
                                                Last activity: {formatTimeAgo(detail.chat.lastActivityAt)}
                                            </div>
                                            <div style={{ marginTop: '6px', color: '#2e7d32', fontSize: '12px', fontWeight: '500' }}>
                                                → Will be linked to this chat
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {withoutChats.length > 0 && (
                                <div>
                                    <div style={{ fontSize: '13px', fontWeight: '600', color: '#1565c0', marginBottom: '8px' }}>
                                        ℹ️ Recipients without open chats ({withoutChats.length}):
                                    </div>
                                    {withoutChats.map((detail, index) => (
                                        <div
                                            key={index}
                                            style={{
                                                padding: '10px',
                                                marginBottom: '8px',
                                                backgroundColor: '#e3f2fd',
                                                border: '1px solid #90caf9',
                                                borderRadius: '4px',
                                                fontSize: '13px'
                                            }}
                                        >
                                            <div style={{ fontWeight: '500', marginBottom: '4px' }}>
                                                {detail.recipient.contact.FullName || detail.recipient.contact.Name}
                                            </div>
                                            <div style={{ color: '#1565c0', fontSize: '12px', fontWeight: '500', marginTop: '6px' }}>
                                                → New chat will be created
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {withErrors.length > 0 && (
                                <div style={{ marginTop: '12px' }}>
                                    <div style={{ fontSize: '13px', fontWeight: '600', color: '#d32f2f', marginBottom: '8px' }}>
                                        ⚠️ Could not load chat info ({withErrors.length}):
                                    </div>
                                    {withErrors.map((detail, index) => (
                                        <div
                                            key={index}
                                            style={{
                                                padding: '10px',
                                                marginBottom: '8px',
                                                backgroundColor: '#ffebee',
                                                border: '1px solid #ef5350',
                                                borderRadius: '4px',
                                                fontSize: '13px'
                                            }}
                                        >
                                            {detail.recipient.contact.FullName || detail.recipient.contact.Name}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export const WhatsAppDlg = React.memo(props => {
    const [previewShown, setPreviewShown] = React.useState(props.previewNotRequired);
    const [selectedTab, setSelectedTab] = React.useState(isMobileOnly ? 0 : 1);
    const [to, setTo] = React.useState([]);
    const [attachments] = React.useState(props.attachments ?? []);
    const [template, setTemplate] = React.useState(null);
    const [templatePreview, setTemplatePreview] = React.useState('');
    const [previewBusy, setPreviewBusy] = React.useState(false);
    const [previewErr, setPreviewErr] = React.useState('');
    const [refreshKey, setRefreshKey] = React.useState(1);
    const [documentsReady, setDocumentsReady] = React.useState((props.attachments?.length ?? 0) === 0);
    const [sendInProgress, setSendInProgress] = React.useState(false);
    const [linkToChats, setLinkToChats] = React.useState(false);

    const elementIDs = {
        preview: "prevDocCtrl"
    };

    // Load initial recipients from props.to
    React.useEffect(() => {
        const loadInitialRecipients = async () => {
            if (!props.to || props.to.length === 0) return;

            const recipientPromises = props.to.map(async (contact) => {
                try {
                    // Load full contact info to get phone numbers
                    const contactInfo = await query_Contact_Info.getFirstOrDefault({ id: contact.ID });

                    // Default to Mobile, then Work, then Home, or null if none exist
                    const defaultNumber = contactInfo.Mobile || contactInfo.WorkPhone || contactInfo.HomePhone || null;

                    // Always add the recipient, even if they have no phone number
                    // The WhatsAppRecipientToken will show them in red if no number
                    return {
                        contact: { ...contact, ...contactInfo },
                        selectedNumber: defaultNumber
                    };
                } catch (err) {
                    console.error('Error loading initial contact info:', err);
                    return null;
                }
            });

            const recipients = (await Promise.all(recipientPromises)).filter(r => r !== null);

            if (recipients.length > 0) {
                setTo(recipients);
            }
        };

        loadInitialRecipients();
    }, [props.to]);

    // Generate template preview when template or recipients change
    React.useEffect(() => {
        let cancelled = false;

        const loadPreview = async () => {
            setPreviewErr("");
            if (!template?.ID) {
                setTemplatePreview("");
                return;
            }

            try {
                setPreviewBusy(true);

                // Get contact ID from first recipient
                const contactID = to.length > 0 ? to[0].contact.ID : null;

                const result = await generateTemplateContent({
                    UseBody: true,
                    TemplateID: template?.ID,
                    ContactID: contactID,
                    JobID: props.jobID || null,
                    ActivityID: null,
                    OpportunityID: null,
                    InvoiceID: null,
                    StatementID: null,
                    SupplyJobID: null,
                    WebLeadID: null,
                });

                let body = "";
                if (Array.isArray(result)) {
                    const item = result[0];
                    body = typeof item === "string" ? item : (item?.Body ?? "");
                } else if (typeof result === "string") {
                    body = result;
                } else if (result && typeof result === "object") {
                    body = result.Body ?? "";
                }

                if (!cancelled) setTemplatePreview(body || "");
            } catch (e) {
                if (!cancelled) {
                    setTemplatePreview("");
                    setPreviewErr("Could not generate preview. Try selecting a contact.");
                }
            } finally {
                if (!cancelled) setPreviewBusy(false);
            }
        };

        const timer = setTimeout(loadPreview, 180);
        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [template?.ID, to, props.jobID]);

    const onTemplateChanged = React.useCallback((value) => {
        const selectedTemplate = props.templates?.find(t => t.ID === value);
        setTemplate(selectedTemplate || null);
        setRefreshKey(k => k + 1);
    }, [props.templates]);

    const onDocumentsReady = React.useCallback(() => {
        setDocumentsReady(true);
    }, []);

    const showPreview = (e) => {
        handleSelect({ selected: isMobileOnly ? 1 : 2 });
        e.promise();
    };

    // Validation rules
    const hasRecipients = to.length > 0;
    const allRecipientsHavePhoneNumbers = to.every(r => !accentUtils.isEmpty(r.selectedNumber));
    const hasTemplate = !accentUtils.isNull(template);

    // Check if selected template has a WhatsApp URL button
    const templateHasUrlButton = React.useMemo(() => {
        if (!template || !template.Body) return false;
        // Check if the body contains a button with href (WhatsApp URL button pattern)
        return /<button\s+href=/i.test(template.Body);
    }, [template]);

    const hasAttachments = attachments.length > 0;

    // Allow send if all conditions are met
    const allowSend = hasRecipients &&
        allRecipientsHavePhoneNumbers &&
        hasTemplate &&
        templateHasUrlButton &&
        ((documentsReady && !sendInProgress) || props.previewNotRequired);

    const handleSend = async () => {
        setSendInProgress(true);

        try {
            // If linkToChats is enabled, look up the chat for each recipient
            let recipientsWithChats = to;

            if (linkToChats) {
                recipientsWithChats = await Promise.all(
                    to.map(async (recipient) => {
                        if (!recipient.selectedNumber) {
                            return { ...recipient, chatID: null };
                        }

                        try {
                            // Normalize phone number
                            const normalizeResult = await query_NormalisePhoneNumber.getFirstOrDefault({
                                phoneNumber: recipient.selectedNumber
                            });
                            const normalizedPhone = normalizeResult?.Normalised || recipient.selectedNumber;

                            // Query for existing open WhatsApp chat
                            const chatResult = await query_ChatAll.getAll({
                                skip: 0,
                                take: 1,
                                contactPoint: normalizedPhone,
                                channel: 'whatsapp',
                                status: ['Open', 'Pending', 'On Hold'],
                                isArchived: false
                            });

                            // Extract chat data
                            let chatID = null;
                            if (Array.isArray(chatResult) && chatResult.length > 0) {
                                const result = chatResult[0];
                                const chats = result?.data || result?.Data || [];
                                if (chats.length > 0) {
                                    const chat = chats[0];
                                    chatID = chat.ChatID || chat.chatID;
                                }
                            }

                            return { ...recipient, chatID };
                        } catch (err) {
                            console.error(`Error looking up chat for ${recipient.contact.FullName}:`, err);
                            return { ...recipient, chatID: null };
                        }
                    })
                );
            }

            const model = {
                templateID: template?.ID,
                recipients: recipientsWithChats,
                attachment: attachments?.[0],
                linkToChats: linkToChats
            };

            const result = await props.onSend(model);

            const allSent = result.every(r => r.IsSent);
            if (allSent) {
                showOK("Success", "WhatsApp messages sent successfully", ["OK"]);
                props.onClose({ sent: true, canceled: false });
            } else {
                const failed = result.filter(r => !r.IsSent);
                showOK("Error", `Failed to send ${failed.length} message(s)`, ["OK"]);
                setSendInProgress(false);
            }
        } catch (error) {
            showOK("Error", error.message || "Failed to send", ["OK"]);
            setSendInProgress(false);
        }
    }

    // Button logic: show "Preview" until previewed, then show "Send"
    const btn = previewShown || !hasAttachments
        ? <AccentButton
            key="2"
            disabled={!allowSend}
            onClick={async (e) => {
                try {
                    await handleSend();
                } finally {
                    e.promise();
                }
            }}
            tagName="sendWhatsApp">
            {t("application_strings.application.buttons.send")}
        </AccentButton>
        : <AccentButton key="3" tagName="showPreview" onClick={showPreview}>{t("application_strings.application.buttons.preview")}</AccentButton>

    const handleSelect = React.useCallback(e => {
        setSelectedTab(e.selected);

        if (e.selected === 2) {
            setPreviewShown(true);
        }
    }, [setSelectedTab, setPreviewShown]);

    return <AccentDialog
        fullscreen
        onClose={props.onClose}
        actionsContent={[btn]}
    >
        <KendoLayout.TabStrip className="whatsAppDlg" selected={selectedTab} onSelect={handleSelect} keepTabsMounted>
            {!isMobileOnly && <KendoLayout.TabStripTab title={t(props.title)} disabled></KendoLayout.TabStripTab>}

            <KendoLayout.TabStripTab title={t(isMobileOnly ? "application_strings.wizards.sendWhatsApp.whatsApp.titleMobile" : "application_strings.wizards.sendWhatsApp.whatsApp.title")}>
                <WhatsAppRecipientSelector to={to} onChange={setTo} />

                {/* Link to Chats Checkbox */}
                {hasRecipients && allRecipientsHavePhoneNumbers && (
                    <div style={{ marginBottom: '20px' }}>
                        <label style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            cursor: 'pointer',
                            fontSize: '14px'
                        }}>
                            <input
                                type="checkbox"
                                checked={linkToChats}
                                onChange={(e) => setLinkToChats(e.target.checked)}
                                style={{ cursor: 'pointer' }}
                            />
                            <span style={{ fontWeight: '500' }}>Link messages to WhatsApp chats</span>
                        </label>
                        <div style={{ fontSize: '12px', color: '#666', marginTop: '4px', marginLeft: '24px' }}>
                            Messages will be sent within existing WhatsApp conversations when available, or new chats will be created
                        </div>

                        <div style={{ marginTop: '12px', marginLeft: '24px' }}>
                            <ChatLinkDetails
                                recipients={to}
                                linkToChats={linkToChats}
                            />
                        </div>
                    </div>
                )}

                {/* Read-only attachments display */}
                {attachments.length > 0 && (
                    <div style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                            {t("application_strings.application.general.emailAttachments")}
                        </label>
                        <div style={{
                            border: '1px solid #ccc',
                            borderRadius: '4px',
                            padding: '12px',
                            backgroundColor: '#f8f9fa'
                        }}>
                            {attachments.map((attachment, index) => (
                                <div
                                    key={index}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        padding: '8px',
                                        marginBottom: index < attachments.length - 1 ? '8px' : '0',
                                        backgroundColor: 'white',
                                        border: '1px solid #dee2e6',
                                        borderRadius: '4px'
                                    }}
                                >
                                    <svg
                                        width="20"
                                        height="20"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="#666"
                                        strokeWidth="2"
                                        style={{ marginRight: '10px', flexShrink: 0 }}
                                    >
                                        <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"></path>
                                        <polyline points="13 2 13 9 20 9"></polyline>
                                    </svg>
                                    <span style={{ flex: 1, fontSize: '14px', color: '#333' }}>
                                        {attachment.Name}
                                    </span>
                                    <span style={{
                                        fontSize: '11px',
                                        color: '#666',
                                        backgroundColor: '#e9ecef',
                                        padding: '2px 8px',
                                        borderRadius: '12px',
                                        marginLeft: '8px'
                                    }}>
                                        {attachment.DisplayType || 'PDF'}
                                    </span>
                                </div>
                            ))}
                        </div>
                        <div style={{ fontSize: '12px', color: '#666', marginTop: '6px' }}>
                            Document will be attached to the WhatsApp message
                        </div>
                    </div>
                )}

                {/* Template Selector */}
                {(props.templates?.length ?? 0) > 0 && (
                    <div style={{ marginBottom: '20px' }}>
                        <AccentComboUnbound
                            key={`t_${refreshKey}`}
                            displayField="Description"
                            valueField="ID"
                            defaultValue={template?.ID}
                            onChange={onTemplateChanged}
                            data={props.templates}
                            label="application_strings.application.general.whatsAppTemplate"
                        />
                        {template && !templateHasUrlButton && (
                            <div style={{
                                fontSize: '12px',
                                color: '#d32f2f',
                                marginTop: '6px',
                                padding: '12px',
                                backgroundColor: '#ffebee',
                                border: '1px solid #ef5350',
                                borderRadius: '4px',
                                lineHeight: '1.5'
                            }}>
                                <div style={{ fontWeight: '600', marginBottom: '8px' }}>
                                    ⚠️ This template does not have a URL button
                                </div>
                                <div style={{ marginBottom: '8px' }}>
                                    To send documents via WhatsApp, you need to add a dynamic URL button to your template in the WhatsApp Manager.
                                </div>
                                <div style={{ marginBottom: '4px', fontWeight: '500' }}>
                                    Setup instructions:
                                </div>
                                <ol style={{ margin: '4px 0 0 20px', paddingLeft: 0 }}>
                                    <li>Open your template in WhatsApp Manager</li>
                                    <li>Add a URL button (Type: Dynamic)</li>
                                    <li>Set the <strong>Website URL</strong> to: <code style={{
                                        backgroundColor: '#fff',
                                        padding: '2px 6px',
                                        borderRadius: '3px',
                                        fontSize: '11px',
                                        border: '1px solid #ef5350'
                                    }}>https://docs.myinsyte.com.au/testrtldocumentstore/&#123;&#123;1&#125;&#125;</code></li>
                                </ol>
                            </div>
                        )}
                        <div style={{ fontSize: '12px', color: '#666', marginTop: '6px' }}>
                            WhatsApp templates must be pre-approved by Meta
                        </div>
                    </div>
                )}

                {/* Template Preview */}
                {template && (
                    <div style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                            Template Preview
                        </label>
                        <div
                            style={{
                                border: '1px solid #ccc',
                                borderRadius: '4px',
                                padding: '12px',
                                minHeight: '140px',
                                backgroundColor: '#fafafa',
                                whiteSpace: 'pre-wrap'
                            }}
                        >
                            {previewBusy ? (
                                <span style={{ color: '#666' }}>Generating preview…</span>
                            ) : templatePreview ? (
                                looksLikeHtml(templatePreview) ? (
                                    <div dangerouslySetInnerHTML={{ __html: templatePreview }} />
                                ) : (
                                    <div style={{ whiteSpace: 'pre-wrap' }}>{templatePreview}</div>
                                )
                            ) : previewErr ? (
                                <span style={{ color: '#666', fontStyle: 'italic' }}>
                                    {previewErr}
                                </span>
                            ) : (
                                <span style={{ color: '#666' }}>
                                    Select a recipient to generate preview
                                </span>
                            )}
                        </div>
                        <div style={{ fontSize: '12px', color: '#666', marginTop: '6px' }}>
                            {to.length === 0 ? (
                                <span style={{ color: '#ff9800' }}>
                                    ⚠️ Select a recipient to see the personalized preview
                                </span>
                            ) : (
                                "Preview shows how the message will appear with merged data"
                            )}
                        </div>
                    </div>
                )}
            </KendoLayout.TabStripTab>

            {hasAttachments && <KendoLayout.TabStripTab title={t(isMobileOnly ? "application_strings.wizards.sendWhatsApp.preview.titleMobile" : "application_strings.wizards.sendWhatsApp.preview.title")}>
                <RTLDocumentGroupPreview
                    elementID={elementIDs.preview}
                    documents={attachments}
                    maxHeight={500}
                    onReady={onDocumentsReady}
                />
            </KendoLayout.TabStripTab>}
        </KendoLayout.TabStrip>
    </AccentDialog>
});